import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, Plus } from 'lucide-react';
import { useMySchoolMembership, useSchoolPrograms, useManageSchool } from '@/hooks/useSchoolApi';

export default function ProgramsManager() {
  const { data: membership } = useMySchoolMembership();
  const schoolId = (membership as any)?.schools?.id || membership?.school_id;
  const { data: programs, isLoading } = useSchoolPrograms(schoolId);
  const manageSchool = useManageSchool();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const canManage = membership?.role === 'school_admin';

  const handleCreate = () => {
    manageSchool.mutate(
      { action: 'create_program', schoolId, name: name.trim(), description: description.trim() || undefined },
      { onSuccess: () => { setDialogOpen(false); setName(''); setDescription(''); } }
    );
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Programs</h2>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Program</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Program</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Automotive Technology" />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={!name.trim() || manageSchool.isPending}>
                  {manageSchool.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!programs?.length ? (
        <p className="text-sm text-muted-foreground text-center py-8">No programs yet.</p>
      ) : (
        <div className="grid gap-3">
          {programs.map((p: any) => (
            <Card key={p.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{p.name}</CardTitle>
              </CardHeader>
              {p.description && (
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">{p.description}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
